import { useState } from "react";
import { MessageCircle, Send, User, Phone, MessageSquare, CheckCircle2 } from "lucide-react";
import { CLINIC, buildWaUrl } from "@/lib/data";
import { toast } from "sonner";

export default function Booking() {
  const [form, setForm] = useState({ nume: "", telefon: "", serviciu: "dental", mesaj: "" });
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nume.trim() || !form.telefon.trim()) {
      toast.error("Te rog completează numele și telefonul.");
      return;
    }
    const isDental = form.serviciu === "dental";
    const dept = isDental ? CLINIC.dental : CLINIC.fizio;
    const msg = isDental
      ? CLINIC.dental.waMessage(form.nume, form.telefon, form.mesaj || "—")
      : CLINIC.fizio.waMessage(form.nume, form.telefon, form.mesaj || "—");
    const url = buildWaUrl(dept.waNumber, msg);
    window.open(url, "_blank", "noopener,noreferrer");
    setSent(true);
    toast.success("Te redirecționăm către WhatsApp...");
    setTimeout(() => setSent(false), 5000);
  };

  return (
    <section
      id="programare"
      data-testid="booking-section"
      className="relative py-20 lg:py-28 bg-gradient-to-br from-brand-soft/60 via-white to-[#F2F9DD]/40 overflow-hidden"
    >
      <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-blue mb-5">
              <span className="w-6 h-px bg-brand-blue" />
              Programează-te
            </span>
            <h2 className="font-display text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.05] font-light text-slate-900 tracking-tight mb-6 text-balance">
              Răspundem pe WhatsApp în <span className="text-gradient-blue font-medium">câteva minute</span>.
            </h2>
            <p className="text-[16px] lg:text-[17px] leading-relaxed text-slate-600 mb-8">
              Completează formularul și mesajul se trimite direct pe WhatsApp către specialistul potrivit, cu datele tale precompletate. Fără telefoane lungi, fără așteptare.
            </p>

            <div className="space-y-4">
              {[
                "Răspuns rapid în timpul programului",
                "Confirmare clară a programării",
                "Indicații și recomandări înainte de vizită",
              ].map((point) => (
                <div key={point} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-aquaDark mt-0.5 shrink-0" />
                  <p className="text-[15px] text-slate-700">{point}</p>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-[32px] p-7 lg:p-10 card-shadow-hover border border-slate-100"
            data-testid="booking-form"
          >
            <div className="space-y-5">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-2" htmlFor="nume">
                  Nume
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    id="nume"
                    type="text"
                    value={form.nume}
                    onChange={(e) => setField("nume", e.target.value)}
                    placeholder="Numele tău"
                    required
                    data-testid="booking-input-nume"
                    className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-blue focus:bg-white transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-2" htmlFor="telefon">
                  Telefon
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    id="telefon"
                    type="tel"
                    value={form.telefon}
                    onChange={(e) => setField("telefon", e.target.value)}
                    placeholder="07XX XXX XXX"
                    required
                    data-testid="booking-input-telefon"
                    className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-blue focus:bg-white transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-2">
                  Aleg serviciul
                </label>
                <div className="grid grid-cols-2 gap-3" role="radiogroup" data-testid="booking-serviciu-group">
                  <button
                    type="button"
                    onClick={() => setField("serviciu", "dental")}
                    data-testid="booking-radio-dental"
                    className={`px-4 py-3.5 rounded-2xl border-2 text-[14px] font-medium transition-all text-left ${
                      form.serviciu === "dental"
                        ? "border-brand-blue bg-brand-soft text-brand-blue"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${form.serviciu === "dental" ? "bg-brand-blue" : "bg-slate-300"}`} />
                      Dental
                    </div>
                    <span className="block text-[11px] mt-1 text-slate-500 font-normal">Stomatologie</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setField("serviciu", "fizio")}
                    data-testid="booking-radio-fizio"
                    className={`px-4 py-3.5 rounded-2xl border-2 text-[14px] font-medium transition-all text-left ${
                      form.serviciu === "fizio"
                        ? "border-brand-aqua bg-[#F2F9DD] text-brand-aquaDark"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${form.serviciu === "fizio" ? "bg-brand-aqua" : "bg-slate-300"}`} />
                      Fizio & Kineto
                    </div>
                    <span className="block text-[11px] mt-1 text-slate-500 font-normal">Recuperare</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-2" htmlFor="mesaj">
                  Mesaj (opțional)
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                  <textarea
                    id="mesaj"
                    value={form.mesaj}
                    onChange={(e) => setField("mesaj", e.target.value)}
                    placeholder="Spune-ne mai multe despre situația ta..."
                    rows={4}
                    data-testid="booking-input-mesaj"
                    className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-blue focus:bg-white transition-colors resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="booking-submit"
                className={`w-full inline-flex items-center justify-center gap-3 text-white font-medium text-[15px] px-6 py-4 rounded-full transition-all shadow-lg hover:-translate-y-0.5 ${
                  form.serviciu === "dental"
                    ? "bg-brand-blue hover:bg-brand-blueDark shadow-brand-blue/25"
                    : "bg-brand-aqua hover:bg-brand-aquaDark shadow-brand-aqua/25"
                }`}
              >
                {sent ? <CheckCircle2 className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                {sent ? "Deschidem WhatsApp..." : "Trimite pe WhatsApp"}
              </button>

              <p className="text-[12px] text-center text-slate-500 leading-relaxed">
                Apăsând „Trimite pe WhatsApp" se va deschide aplicația WhatsApp cu mesajul precompletat.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
